import axios from 'axios';
import logger from './logger';

// Use a dedicated client so explicit logout cannot recursively enter the
// configured Axios response interceptor.
const logoutClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
});

export const AUTH_CORRELATION = Object.freeze({
  EXACT_TOKEN: 'exact_token',
  VERIFIED_SESSION: 'verified_session',
});

export const WS_CLOSE_REASONS = Object.freeze({
  SESSION_TOKEN_INVALID: 'WS_SESSION_TOKEN_INVALID',
  SESSION_INVALID_OR_REVOKED: 'WS_SESSION_INVALID_OR_REVOKED',
  USER_NOT_FOUND: 'WS_USER_NOT_FOUND',
  USER_NOT_ACTIVE: 'WS_USER_NOT_ACTIVE',
  TRANSPORT_AUTH_MISSING: 'WS_TRANSPORT_AUTH_MISSING',
  POLICY_UNKNOWN_ROLE: 'WS_POLICY_UNKNOWN_ROLE',
  POLICY_SCOPE_MISSING: 'WS_POLICY_SCOPE_MISSING',
  POLICY_TRIP_SCOPE_INVALID: 'WS_POLICY_TRIP_SCOPE_INVALID',
  POLICY_ENDPOINT_ROLE_MISMATCH: 'WS_POLICY_ENDPOINT_ROLE_MISMATCH',
  POLICY_ENTITY_MEMBERSHIP_REQUIRED: 'WS_POLICY_ENTITY_MEMBERSHIP_REQUIRED',
  POLICY_TRIP_ASSIGNMENT_REQUIRED: 'WS_POLICY_TRIP_ASSIGNMENT_REQUIRED',
  POLICY_TRIP_ENTITLEMENT_REQUIRED: 'WS_POLICY_TRIP_ENTITLEMENT_REQUIRED',
});

const VERIFIED_SESSION_REASONS = new Set([
  WS_CLOSE_REASONS.SESSION_INVALID_OR_REVOKED,
  WS_CLOSE_REASONS.USER_NOT_FOUND,
  WS_CLOSE_REASONS.USER_NOT_ACTIVE,
  'Token expired',
  'User not found',
  'User account is not active',
]);

const EXACT_TOKEN_REASONS = new Set([
  WS_CLOSE_REASONS.SESSION_TOKEN_INVALID,
  // Older servers used this phrase before and after database evaluation, so it
  // cannot safely authorize same-jti correlation.
  'Token is invalid or revoked',
  'Invalid or expired token',
]);

const STATIC_POLICY_REASONS = new Set([
  WS_CLOSE_REASONS.POLICY_UNKNOWN_ROLE,
  WS_CLOSE_REASONS.POLICY_SCOPE_MISSING,
  WS_CLOSE_REASONS.POLICY_TRIP_SCOPE_INVALID,
  WS_CLOSE_REASONS.POLICY_ENDPOINT_ROLE_MISMATCH,
  'Access denied - not a member of this entity',
  'Access denied - unrecognized role',
  'Access denied - admin role required',
  'Access denied - rider role required',
  'Admin users should use /admin/bookings endpoint',
  'Missing entity or trip ID',
  'Missing entity ID',
  'Trip not found for entity',
]);

const MUTABLE_POLICY_REASONS = new Set([
  WS_CLOSE_REASONS.POLICY_ENTITY_MEMBERSHIP_REQUIRED,
  WS_CLOSE_REASONS.POLICY_TRIP_ASSIGNMENT_REQUIRED,
  WS_CLOSE_REASONS.POLICY_TRIP_ENTITLEMENT_REQUIRED,
  'Access denied - not assigned to this trip',
  'Access denied to this trip',
]);

const decodeTokenPayload = (token) => {
  if (typeof token !== 'string') {
    return null;
  }

  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) {
    return null;
  }

  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

export const getTokenSessionId = (token) => {
  const jti = decodeTokenPayload(token)?.jti;
  return typeof jti === 'string' && jti.length > 0 ? jti : null;
};

export const tokensMatchCorrelation = (expectedToken, currentToken, correlation) => {
  if (!expectedToken || !currentToken) {
    return false;
  }

  if (correlation === AUTH_CORRELATION.EXACT_TOKEN) {
    return expectedToken === currentToken;
  }

  if (correlation === AUTH_CORRELATION.VERIFIED_SESSION) {
    const expectedSessionId = getTokenSessionId(expectedToken);
    return Boolean(
      expectedSessionId
      && expectedSessionId === getTokenSessionId(currentToken)
    );
  }

  return false;
};

export const classifyWebSocketClose = (code, reason = '') => {
  if (code !== 1008) {
    return { category: 'transport', correlation: null };
  }

  if (EXACT_TOKEN_REASONS.has(reason)) {
    return {
      category: 'session_invalid',
      correlation: AUTH_CORRELATION.EXACT_TOKEN,
    };
  }

  if (VERIFIED_SESSION_REASONS.has(reason)) {
    return {
      category: 'session_invalid',
      correlation: AUTH_CORRELATION.VERIFIED_SESSION,
    };
  }

  if (reason === WS_CLOSE_REASONS.TRANSPORT_AUTH_MISSING || reason === 'Missing authentication token') {
    return { category: 'transport_missing', correlation: null };
  }

  if (MUTABLE_POLICY_REASONS.has(reason)) {
    return { category: 'mutable_denial', correlation: null };
  }

  if (STATIC_POLICY_REASONS.has(reason) || !reason) {
    return { category: 'static_denial', correlation: null };
  }

  // Unknown policy violations fail closed for the socket while preserving the
  // authenticated HTTP session. They must never fall into a reconnect loop.
  return { category: 'static_denial', correlation: null };
};

let logoutInFlight = null;

const clearBrowserSession = () => {
  localStorage.clear();
  window.location.href = '/login';
};

// Explicit user logout retains the server-side revocation request.
export const handleLogout = async (reason = 'user_logout') => {
  if (logoutInFlight) {
    return logoutInFlight;
  }

  logoutInFlight = (async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await logoutClient.post(
          '/api/users/logout',
          { reason },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        logger.error('Error during logout', error);
      }
    }

    clearBrowserSession();
    return true;
  })();

  try {
    return await logoutInFlight;
  } finally {
    logoutInFlight = null;
  }
};

// Server-declared terminal failures skip the logout API: the credential is
// already unusable. Provenance guards prevent a late HTTP/WebSocket failure
// from clearing a replacement login.
export const handleTerminalSessionFailure = async ({
  expectedToken,
  correlation,
} = {}) => {
  const currentToken = localStorage.getItem('token');
  if (!tokensMatchCorrelation(expectedToken, currentToken, correlation)) {
    return false;
  }

  // This path has no server work to coalesce. Keep the provenance check and
  // clear synchronous so one session's in-flight cleanup cannot swallow a
  // terminal failure for a different session established immediately after it.
  clearBrowserSession();
  return true;
};
